import { latLngNumberFromLatLngStr } from './googleMapTypes';
import { LatLngStr } from './stopTypes';

// Bigger numbers are MORE zoomed in.
// Only integers are allowed; decimals get rounded.
export const DEFAULT_ZOOM = 4;
export const DEFAULT_CENTER = {
    // This is approximately the geographic center of the continental United States
    lat: 39.8283,
    lng: -98.5795,
};

export const autofitMap = ({
    map,
    maps,
    latLngs,
}: {
    map: google.maps.Map | null;
    maps: typeof google.maps | null;
    latLngs: LatLngStr[];
}): void => {
    if (!map || !maps) {
        return;
    }

    // If there's no lat/lng pairs, then Google Maps puts us in the middle of the ocean.
    //  So... we don't adjust anything in that case.
    if (latLngs.length === 0) {
        return;
    }

    // Create a special LatLngBounds() object.
    const bounds = new maps.LatLngBounds();
    latLngs.forEach((latLng) => bounds.extend(latLngNumberFromLatLngStr(latLng)));

    // Once `map.fitBounds()` has done its thing, we want to adjust it a bit.
    //  But `map.fitBounds()` is an asynchronous function. So to adjust the zoom
    //  after the map has been fit to the bounds, we use this event listener.
    // By using `addListenerOnce()`, this listener runs only for the next
    //  'bounds_changed' event and is automatically unregistered. This way,
    //  we don't get stuck in an infinite loop.
    // Source: https://stackoverflow.com/a/22712105/1292652
    maps.event.addListenerOnce(map, 'bounds_changed', () => {
        const currentZoom = map.getZoom();
        if (currentZoom === undefined) {
            return;
        }

        // If we don't like the way map.fitBounds() zooms, we can adjust it.
        //  Remember: bigger numbers are MORE zoomed in, and only integers are allowed.
        // For now, I find the default behaviour okay, so the adjustment factor is 0.
        const adjustmentFactor = 0;
        map.setZoom(currentZoom - adjustmentFactor);

        // Don't let the map get TOO zoomed in.
        //
        // When there's only one lat/lng, Google Maps wants to zoom VERY close,
        //  (to 22). We set a limit.
        //
        const MAX_ZOOM_FOR_AUTOFIT = 15;
        if (currentZoom > MAX_ZOOM_FOR_AUTOFIT) {
            map.setZoom(MAX_ZOOM_FOR_AUTOFIT);
        }
    });

    // This adjusts both the center and the zoom.
    map.fitBounds(bounds);
};
